import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { PageTitle } from '../CoachDashboard/styles';
import { chatAPI } from '../../services/api';
import ReactMarkdown from 'react-markdown';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 180px);
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  margin-top: 1rem;

  @media (max-width: 768px) {
    height: calc(100vh - 160px);
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: #fff;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f5f5f5;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--accent-orange);
    border-radius: 4px;

    &:hover {
      background: var(--accent-orange-dark);
    }
  }
`;

const Message = styled.div<{ isUser: boolean }>`
  display: flex;
  justify-content: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  margin-bottom: 0.5rem;
`;

const MessageBubble = styled.div<{ isUser: boolean }>`
  max-width: 75%;
  padding: 0.875rem 1rem;
  border-radius: 16px;
  background: ${props => props.isUser ? 'var(--accent-orange)' : 'var(--bg-sidebar)'};
  color: ${props => props.isUser ? '#fff' : '#000'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  word-wrap: break-word;
  line-height: 1.4;
  font-size: 0.75rem;

  @media (max-width: 768px) {
    max-width: 85%;
    padding: 0.75rem 0.875rem;
    font-size: 0.7rem;
  }

  p {
    margin: 0 0 0.4rem 0;
    color: ${props => props.isUser ? '#fff' : '#000'};
    &:last-child {
      margin-bottom: 0;
    }
  }

  ul, ol {
    margin: 0.4rem 0;
    padding-left: 1.2rem;
    color: ${props => props.isUser ? '#fff' : '#000'};
  }

  li {
    color: ${props => props.isUser ? '#fff' : '#000'};
  }

  code {
    background: ${props => props.isUser ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
    padding: 0.15rem 0.3rem;
    border-radius: 4px;
    font-size: 0.7em;
    color: ${props => props.isUser ? '#fff' : '#000'};
  }

  pre {
    background: ${props => props.isUser ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
    padding: 0.6rem;
    border-radius: 8px;
    overflow-x: auto;
    margin: 0.4rem 0;
    color: ${props => props.isUser ? '#fff' : '#000'};
  }

  strong, b {
    color: ${props => props.isUser ? '#fff' : '#000'};
  }

  em, i {
    color: ${props => props.isUser ? '#fff' : '#000'};
  }
`;

const InputContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: #fff;
`;

const Input = styled.textarea`
  flex: 1;
  padding: 0.75rem 0.875rem;
  border: 2px solid var(--card-border);
  border-radius: 12px;
  font-size: 0.75rem;
  font-family: 'Poppins', sans-serif;
  background: var(--bg-sidebar);
  color: var(--text-gray);
  resize: none;
  min-height: 60px;
  max-height: 150px;
  line-height: 1.5;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-orange);
    box-shadow: 0 0 0 3px rgba(255, 150, 0, 0.1);
  }

  &::placeholder {
    color: var(--text-gray);
    opacity: 0.5;
  }

  @media (max-width: 768px) {
    min-height: 70px;
    padding: 0.875rem 1rem;
  }
`;

const SendButton = styled.button<{ disabled: boolean }>`
  padding: 0.75rem 1.75rem;
  background: ${props => props.disabled ? 'var(--bg-tertiary)' : 'var(--accent-orange)'};
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 900;
  font-family: 'Poppins', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.2s, transform 0.2s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: var(--accent-orange-dark);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1.25rem;
  }
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-gray);
  opacity: 0.7;
  font-size: 0.9rem;
  padding: 0.5rem 0;
`;

const AnimatedDots = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;

  span {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: var(--text-gray);
    opacity: 0.7;
    animation: dotPulse 1.4s ease-in-out infinite;

    &:nth-child(1) {
      animation-delay: 0s;
    }

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }

  @keyframes dotPulse {
    0%, 80%, 100% {
      opacity: 0.3;
      transform: scale(0.8);
    }
    40% {
      opacity: 0.7;
      transform: scale(1);
    }
  }
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  background: rgba(255, 0, 0, 0.1);
  border: 1px solid rgba(255, 0, 0, 0.3);
  border-radius: 8px;
  color: #ff4444;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const WelcomeMessage = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: var(--text-gray);
  opacity: 0.8;

  h3 {
    color: var(--accent-orange);
    margin-bottom: 1rem;
    font-size: 1.5rem;
    font-weight: 900;
  }

  p {
    margin: 0.5rem 0;
    font-size: 1rem;
    line-height: 1.6;
  }
`;

interface Message {
  role: 'user' | 'model';
  content: string;
}

const CoachChat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setError(null);

    // Add user message to UI immediately
    const newUserMessage: Message = { role: 'user', content: userMessage };
    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      // Prepare conversation history for API
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await chatAPI.sendMessage(userMessage, conversationHistory);

      // Add AI response to messages
      const aiMessage: Message = { role: 'model', content: response.message };
      setMessages(prev => [...prev, aiMessage]);
    } catch (err: any) {
      console.error('Chat error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to send message. Please try again.');
      // Remove the user message if there was an error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
      // Focus input after sending
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    // Auto-resize textarea
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`;
  };

  return (
    <Container>
      <PageTitle style={{ textAlign: 'center' }}>AI MATE</PageTitle>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ChatContainer>
        <MessagesContainer>
          {messages.length === 0 ? (
            <WelcomeMessage>
              <h3>Welcome to sjMate AI Assistant!</h3>
              <p>I'm here to help you with ski jumping coaching questions.</p>
              <p>Ask me about techniques, training methods, athlete performance, equipment, or anything related to ski jumping coaching!</p>
            </WelcomeMessage>
          ) : (
            messages.map((message, index) => (
              <Message key={index} isUser={message.role === 'user'}>
                <MessageBubble isUser={message.role === 'user'}>
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                </MessageBubble>
              </Message>
            ))
          )}
          
          {isLoading && (
            <Message isUser={false}>
              <MessageBubble isUser={false}>
                <LoadingIndicator>
                  <AnimatedDots>
                    <span></span>
                    <span></span>
                    <span></span>
                  </AnimatedDots>
                </LoadingIndicator>
              </MessageBubble>
            </Message>
          )}
          
          <div ref={messagesEndRef} />
        </MessagesContainer>

        <InputContainer>
          <Input
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about ski jumping coaching..."
            disabled={isLoading}
            rows={1}
          />
          <SendButton
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
          >
            SEND
          </SendButton>
        </InputContainer>
      </ChatContainer>
    </Container>
  );
};

export default CoachChat;

